/**
 * 30 分コマ (= 1 日 48 コマ) ⇄ 開始時刻ラベル "HH:MM" の変換 canonical module。
 *
 * **コマ番号 (1-origin, 1〜48) が canonical。** 0-origin の配列 index を扱う
 * 関数は呼び出し側が origin を推測しなくていいよう、名前に `Index` を含める:
 *
 * - [[komaNumberToTime]] … **1-origin** (コマ 1 → "00:00", コマ 48 → "23:30")。
 *                           入札 chart など 1 始まりのコマ番号用。canonical。
 * - [[dateToKomaNumber]] … **1-origin** ([[komaNumberToTime]] の逆変換を `Date`
 *                           で行う版)。JST 壁時計の「現在コマ」判定用
 *                           (= 表示 + GC 境界の canonical)。
 * - [[komaIndexToTime]]  … **0-origin** (index 0 → "00:00", index 47 →
 *                           "23:30")。同時同量 / 供給力系 chart の 0 始まり
 *                           index 用 ([[komaNumberToTime]] の adapter)。
 * - [[timeToKomaNumber]] … **1-origin** ([[komaNumberToTime]] の逆変換)。
 *
 * 開始時刻ラベルから「そのコマの終了時刻 / 帯」を導く [[komaEndTime]] /
 * [[komaTimeRange]] / [[komaNumberTimeRange]] も同じ写像に属するのでここに
 * 置く (= コマ 48 を 24:00 とするか 00:00 とするかを画面ごとに再実装させない)。
 *
 * [[KOMA_TIME_LABELS]] は 48 コマの開始時刻ラベル配列 (X 軸・行見出し用)。
 */
import { KOMA_COUNT } from '~/constants/koma'
import { JST_OFFSET_MS } from '~/models/dateTime'

/**
 * コマ番号 (1-origin) を開始時刻ラベル "HH:MM" に変換する (canonical)。
 * コマ 1 → "00:00", コマ 2 → "00:30", ..., コマ 48 → "23:30"。
 *
 * コマ 49 は **"24:00" (受渡日単位で折り返さない。[[komaEndTime]] と同じ方針)**。
 * 当日終端の表現に使われる。
 */
export function komaNumberToTime(koma: number): string {
  const hour = Math.floor((koma - 1) / 2)
  const minute = (koma - 1) % 2 === 0 ? '00' : '30'
  return `${String(hour).padStart(2, '0')}:${minute}`
}

/**
 * コマ index (0-origin) を開始時刻ラベル "HH:MM" に変換する
 * ([[komaNumberToTime]] の 0-origin adapter)。
 * index 0 → "00:00", index 1 → "00:30", ..., index 47 → "23:30"。
 */
export function komaIndexToTime(index: number): string {
  return komaNumberToTime(index + 1)
}

/**
 * 開始時刻ラベル "HH:MM" をコマ番号 (1-origin) に変換する
 * ([[komaNumberToTime]] の逆)。
 * "00:00" → 1, "00:30" → 2, ..., "23:30" → 48。
 *
 * コマ番号列を持たない chart データ (同時同量 一覧の `KomaData`) で、X 軸ラベルから
 * コマ番号を復元するために使う。
 */
export function timeToKomaNumber(time: string): number {
  const [hour, minute] = time.split(':').map(Number)
  return hour * 2 + (minute >= 30 ? 2 : 1)
}

/**
 * `Date` (= 絶対時刻) が **JST の何コマ目か**を コマ番号 (1-origin, 1〜48) で返す
 * ([[komaNumberToTime]] の逆変換を時刻ラベルを経由せずに行う版)。
 * JST 00:00〜00:29 → 1, 00:30〜00:59 → 2, ..., 23:30〜23:59 → 48。
 *
 * コマは JST の壁時計で定義される業務概念のため、端末ローカル TZ ではなく JST に
 * 変換してから判定する。
 *
 * 用途: 「今この瞬間が何コマ目か」(テロップバーの現在コマ / 時間前市場の GC 境界)。
 */
export function dateToKomaNumber(d: Date): number {
  const jst = new Date(d.getTime() + JST_OFFSET_MS)
  return jst.getUTCHours() * 2 + (jst.getUTCMinutes() >= 30 ? 2 : 1)
}

/**
 * 開始時刻ラベル "HH:MM" の 30 分後 (= そのコマの終了時刻)。
 *
 * コマ 48 は **"24:00" で止め、"00:00" へ折り返さない**。計画・実績・
 * インバランスはすべて受渡日単位なので、翌日へ回ると別の受渡日のコマに読める
 * (domain-knowledge §1 のコマ表も `48 | 23:30〜24:00`、design charts.js /
 * bg-data.js の時間レンジ生成も折り返さない)。
 */
export function komaEndTime(startTime: string): string {
  const [hour, minute] = startTime.split(':').map(Number)
  const total = hour * 60 + minute + 30
  const endHour = Math.floor(total / 60)
  const endMinute = total % 60
  return `${endHour < 10 ? '0' : ''}${endHour}:${endMinute === 0 ? '00' : '30'}`
}

/**
 * コマの帯を "HH:MM～HH:MM" にする ([[komaEndTime]] の表記違い)。
 * 需要ピーク (サマリーバー / 一覧カードのピルバッジ) が使う。
 */
export function komaTimeRange(startTime: string): string {
  return `${startTime}～${komaEndTime(startTime)}`
}

/**
 * コマ番号 (1-origin) の帯 "HH:MM<sep>HH:MM"。区切りは画面ごとに異なる
 * (既定 "～" = [[komaTimeRange]] と同じ、他に "〜" / "-")。コマ 48 は
 * "23:30～24:00" (折り返さない)。
 */
export function komaNumberTimeRange(koma: number, separator = '～'): string {
  const start = komaNumberToTime(koma)
  return `${start}${separator}${komaEndTime(start)}`
}

/** 48 コマの開始時刻ラベル 00:00〜23:30。X 軸・行見出し用。 */
export const KOMA_TIME_LABELS: readonly string[] = Object.freeze(
  Array.from({ length: KOMA_COUNT }, (_, i) => komaNumberToTime(i + 1)),
)
